/**
 * MoveMeetingDialog — modal that re-parents a meeting under a
 * different project. Used from the project page's meeting rows so a
 * mistakenly-filed meeting can be moved with two clicks instead of
 * unlocking + re-editing.
 *
 * Server contract:
 *   PATCH /meetings/:id { projectId, expectedVersion }
 *
 * The PATCH handler already supports projectId in its update set;
 * see apps/api/src/routes/meetings.ts. The handler also mirrors the
 * new project name into structuredContent.project (when no alias
 * override is set) so the rendered minutes pick up the new project
 * label without a re-transform.
 *
 * Behavior notes:
 *   - Allowed for both draft and finalized meetings (mistakes happen
 *     post-finalize and we want a fast fix path).
 *   - The modal blocks moving to the SAME project the meeting is
 *     already in — that's a no-op.
 *   - 409 (version conflict) rejection is handled gracefully —
 *     prompts the user to refresh the project page and try again.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/Modal.tsx';
import { ProjectPicker } from './ProjectPicker.tsx';
import { api, ApiError } from '../../lib/apiClient.ts';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  meeting: {
    id: string;
    title: string;
    projectId: string;
    projectName?: string | null;
    version: number;
  };
  /** Fires after a successful move. The parent typically uses this to
   *  invalidate React Query caches so the project page meeting list
   *  refreshes (the moved meeting drops out of the source project's
   *  list and shows up under the destination project). */
  onMoved?: () => void;
}

interface PickedProject {
  projectId?: string;
  projectName?: string;
  label: string;
}

export function MoveMeetingDialog({ isOpen, onClose, meeting, onMoved }: Props) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<PickedProject>({ label: '' });
  const [validationError, setValidationError] = useState<string | null>(null);

  const moveMutation = useMutation({
    mutationFn: async (destinationProjectId: string) => {
      return api.patch(`/meetings/${meeting.id}`, {
        projectId: destinationProjectId,
        expectedVersion: meeting.version,
      });
    },
    onSuccess: () => {
      // Refresh every meeting + project list. The moved meeting
      // changes BOTH the source project's count (down by 1) and the
      // destination project's count (up by 1).
      qc.invalidateQueries({ queryKey: ['meetings'] });
      qc.invalidateQueries({ queryKey: ['projects-with-counts'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      onMoved?.();
      // Reset local state for the next open
      setPicked({ label: '' });
      setValidationError(null);
      onClose();
    },
  });

  function handleMove() {
    setValidationError(null);
    if (!picked.projectId) {
      setValidationError(
        'Pick an existing project from the dropdown. To create a new project for this meeting, use "+ New project" on the dashboard first.',
      );
      return;
    }
    if (picked.projectId === meeting.projectId) {
      setValidationError(
        'That is already this meeting\'s project. Pick a different one.',
      );
      return;
    }
    moveMutation.mutate(picked.projectId);
  }

  function handleClose() {
    if (moveMutation.isPending) return;
    setPicked({ label: '' });
    setValidationError(null);
    onClose();
  }

  const apiError = moveMutation.error;
  const apiErrorMessage = (() => {
    if (!apiError) return null;
    if (apiError instanceof ApiError) {
      // Special-case the optimistic-concurrency 409 so the user gets
      // a clearer next step than the raw server message.
      if (apiError.status === 409) {
        return 'This meeting was edited in another tab while this dialog was open. Refresh the project page and try the move again.';
      }
      return apiError.message;
    }
    if (apiError instanceof Error) return apiError.message;
    return 'Move failed. Try again.';
  })();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      ariaLabel="Move meeting to another project"
      size="md"
    >
      <ModalHeader
        title="Move meeting"
        subtitle={
          <>
            Re-parent <span className="font-medium text-charcoal">{meeting.title}</span>{' '}
            from{' '}
            <span className="font-medium text-charcoal">
              {meeting.projectName ?? 'its current project'}
            </span>{' '}
            to a different project.
          </>
        }
        onClose={handleClose}
      />
      <ModalBody>
        <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-pine">
          Destination project
        </label>
        <ProjectPicker
          value={picked.label}
          onChange={(next) => {
            setPicked(next);
            setValidationError(null);
          }}
          placeholder="Search existing projects…"
        />
        {validationError && (
          <div className="mt-3 rounded-lg border border-terra-cotta/30 bg-terra-cotta/10 px-3 py-2 text-[12px] text-terra-cotta">
            {validationError}
          </div>
        )}
        {apiErrorMessage && (
          <div className="mt-3 rounded-lg border border-terra-cotta/30 bg-terra-cotta/10 px-3 py-2 text-[12px] text-terra-cotta">
            {apiErrorMessage}
          </div>
        )}
        <p className="mt-4 text-[12px] leading-relaxed text-charcoal/60">
          The meeting&apos;s rendered minutes update to show the new
          project name. If the meeting has a project alias set, the
          alias keeps showing in its place. Counts on both projects
          adjust automatically.
        </p>
      </ModalBody>
      <ModalFooter>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={moveMutation.isPending}
            className="rounded-full border border-charcoal/25 px-4 py-1.5 text-[13px] text-charcoal transition-colors hover:bg-cloud/50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleMove}
            disabled={moveMutation.isPending || !picked.projectId}
            className="rounded-full bg-pine px-4 py-1.5 text-[13px] font-medium text-cloud shadow-sm transition-colors hover:bg-pine/90 disabled:opacity-50"
          >
            {moveMutation.isPending ? 'Moving…' : 'Move meeting'}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
